import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  type MtSession,
  type MtMember,
  type MtGroup,
  type MtGroupMember,
  type MtCrossOrgRule,
  exchangeToken,
  listMembers,
  grantRole,
  revokeRole,
  listGroups,
  createGroup,
  updateGroup,
  deleteGroup,
  listGroupMembers,
  addGroupMember,
  removeGroupMember,
  listCrossOrg,
  createCrossOrgRule,
  deleteCrossOrgRule,
} from "@/lib/manageTeamApi";

const TOOL_ROLES = ["tool-admin", "tool-manager", "tool-member", "tool-viewer", "denied"];

function roleBadgeClass(role: string): string {
  switch (role) {
    case "tool-admin": return "bg-violet-100 text-violet-800";
    case "tool-manager": return "bg-blue-100 text-blue-800";
    case "tool-member": return "bg-green-100 text-green-800";
    case "tool-viewer": return "bg-slate-100 text-slate-700";
    case "denied": return "bg-red-100 text-red-800";
    case "super-admin":
    case "suite-admin": return "bg-yellow-100 text-yellow-800";
    default: return "bg-slate-100 text-slate-500";
  }
}

function sourceBadge(src: string): string {
  switch (src) {
    case "super-admin": return "Platform";
    case "suite-admin": return "Suite-Admin";
    case "direct": return "Direct";
    case "group": return "Group";
    case "org": return "Org";
    case "xorg": return "Cross-Org";
    default: return "";
  }
}

// ---------------------------------------------------------------------------
// Members tab
// ---------------------------------------------------------------------------

function MembersTab({ session }: { session: MtSession }) {
  const [members, setMembers] = useState<MtMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [changing, setChanging] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { members: data } = await listMembers(session);
      setMembers(data);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to load members");
    } finally {
      setLoading(false);
    }
  }, [session]);

  useEffect(() => { void load(); }, [load]);

  async function handleRoleChange(userId: string, role: string) {
    setChanging(userId);
    try {
      if (role === "__revoke__") {
        await revokeRole(session, userId);
        toast.success("Role revoked — user will fall back to org default");
      } else {
        await grantRole(session, userId, role);
        toast.success("Role updated");
      }
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to update role");
    } finally {
      setChanging(null);
    }
  }

  if (loading) return <div className="py-12 text-center text-sm text-slate-500">Loading members…</div>;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-left text-xs font-medium uppercase tracking-wide text-slate-500">
            <th className="py-3 pr-4">Member</th>
            <th className="py-3 pr-4">Org role</th>
            <th className="py-3 pr-4">Tool role</th>
            <th className="py-3 pr-4">Source</th>
            {session.canWrite && <th className="py-3">Change role</th>}
          </tr>
        </thead>
        <tbody className="divide-y">
          {members.map((m) => {
            const src = sourceBadge(m.toolRoleSource);
            const isProtected = ["super-admin", "suite-admin"].includes(m.toolRoleSource);
            return (
              <tr key={m.userId} className="hover:bg-slate-50/60">
                <td className="py-3 pr-4">
                  <div className="flex items-center gap-2.5">
                    {m.imageUrl && (
                      <img src={m.imageUrl} alt="" className="h-7 w-7 rounded-full object-cover" />
                    )}
                    <div>
                      <p className="font-medium text-slate-800">{m.displayName}</p>
                      {m.email && <p className="text-xs text-slate-400">{m.email}</p>}
                    </div>
                  </div>
                </td>
                <td className="py-3 pr-4 text-slate-600">{m.orgRole ?? "—"}</td>
                <td className="py-3 pr-4">
                  <span className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${roleBadgeClass(m.toolRole)}`}>
                    {m.toolRole}
                  </span>
                </td>
                <td className="py-3 pr-4">
                  {src && (
                    <span className="inline-flex rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                      {src}
                    </span>
                  )}
                </td>
                {session.canWrite && (
                  <td className="py-3">
                    {isProtected ? (
                      <span className="text-xs text-slate-400">Protected</span>
                    ) : (
                      <Select
                        disabled={changing === m.userId}
                        onValueChange={(v) => void handleRoleChange(m.userId, v)}
                      >
                        <SelectTrigger className="h-7 w-36 text-xs">
                          <SelectValue placeholder="Set role…" />
                        </SelectTrigger>
                        <SelectContent>
                          {TOOL_ROLES.map((r) => (
                            <SelectItem key={r} value={r} className="text-xs">{r}</SelectItem>
                          ))}
                          <SelectItem value="__revoke__" className="text-xs text-red-600">
                            Revoke override
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      {members.length === 0 && (
        <p className="py-10 text-center text-sm text-slate-400">No members found.</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Groups tab
// ---------------------------------------------------------------------------

function GroupsTab({ session }: { session: MtSession }) {
  const [groups, setGroups] = useState<MtGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);
  const [managingGroup, setManagingGroup] = useState<MtGroup | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { groups: data } = await listGroups(session);
      setGroups(data);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to load groups");
    } finally {
      setLoading(false);
    }
  }, [session]);

  useEffect(() => { void load(); }, [load]);

  async function handleCreate() {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      await createGroup(session, newName.trim());
      toast.success("Group created");
      setNewName("");
      setShowCreate(false);
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to create group");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(g: MtGroup) {
    if (!confirm(`Delete group "${g.name}"? This will remove all members.`)) return;
    try {
      await deleteGroup(session, g.id);
      toast.success("Group deleted");
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to delete group");
    }
  }

  async function handleRoleChange(g: MtGroup, role: string) {
    try {
      await updateGroup(session, g.id, { productRole: role === "__none__" ? null : role });
      toast.success("Group role updated");
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to update role");
    }
  }

  if (loading) return <div className="py-12 text-center text-sm text-slate-500">Loading groups…</div>;

  return (
    <div className="space-y-4">
      {session.canWrite && (
        <div className="flex justify-end">
          <Button size="sm" onClick={() => setShowCreate(true)}>+ New group</Button>
        </div>
      )}

      {groups.length === 0 && (
        <p className="py-10 text-center text-sm text-slate-400">No groups yet.{session.canWrite ? " Create one to manage bulk access." : ""}</p>
      )}

      <div className="space-y-2">
        {groups.map((g) => (
          <div key={g.id} className="flex items-center gap-4 rounded-lg border px-4 py-3">
            <div className="min-w-0 flex-1">
              <p className="font-medium text-slate-800">{g.name}</p>
              {g.description && <p className="text-xs text-slate-400">{g.description}</p>}
            </div>
            <span className="text-xs text-slate-500">{g.memberCount} member{g.memberCount !== 1 ? "s" : ""}</span>
            <div className="flex items-center gap-2">
              {session.canWrite ? (
                <Select
                  value={g.productRole ?? "__none__"}
                  onValueChange={(v) => void handleRoleChange(g, v)}
                >
                  <SelectTrigger className="h-7 w-36 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__" className="text-xs text-slate-400">No role</SelectItem>
                    {TOOL_ROLES.map((r) => (
                      <SelectItem key={r} value={r} className="text-xs">{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <span className="text-xs text-slate-500">{g.productRole ?? "No role"}</span>
              )}
              <Button size="sm" variant="outline" onClick={() => setManagingGroup(g)}>
                Members
              </Button>
              {session.canWrite && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-red-600 hover:text-red-700"
                  onClick={() => void handleDelete(g)}
                >
                  Delete
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New group</DialogTitle>
          </DialogHeader>
          <Input
            placeholder="Group name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && void handleCreate()}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={() => void handleCreate()} disabled={saving || !newName.trim()}>
              {saving ? "Creating…" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage members dialog */}
      {managingGroup && (
        <GroupMembersDialog
          session={session}
          group={managingGroup}
          onClose={() => setManagingGroup(null)}
        />
      )}
    </div>
  );
}

function GroupMembersDialog({
  session,
  group,
  onClose,
}: {
  session: MtSession;
  group: MtGroup;
  onClose: () => void;
}) {
  const [members, setMembers] = useState<MtGroupMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [addUserId, setAddUserId] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { members: data } = await listGroupMembers(session, group.id);
      setMembers(data);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to load members");
    } finally {
      setLoading(false);
    }
  }, [session, group.id]);

  useEffect(() => { void load(); }, [load]);

  async function handleAdd() {
    if (!addUserId.trim()) return;
    setSaving(true);
    try {
      await addGroupMember(session, group.id, addUserId.trim());
      toast.success("Member added");
      setAddUserId("");
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to add member");
    } finally {
      setSaving(false);
    }
  }

  async function handleRemove(userId: string) {
    try {
      await removeGroupMember(session, group.id, userId);
      toast.success("Member removed");
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to remove member");
    }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Members — {group.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {session.canWrite && (
            <div className="flex gap-2">
              <Input
                placeholder="Clerk user ID to add…"
                value={addUserId}
                onChange={(e) => setAddUserId(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && void handleAdd()}
                className="text-sm"
              />
              <Button size="sm" onClick={() => void handleAdd()} disabled={saving || !addUserId.trim()}>
                Add
              </Button>
            </div>
          )}
          {loading ? (
            <p className="py-4 text-center text-sm text-slate-400">Loading…</p>
          ) : members.length === 0 ? (
            <p className="py-4 text-center text-sm text-slate-400">No members yet.</p>
          ) : (
            <ul className="divide-y text-sm">
              {members.map((m) => (
                <li key={m.clerkUserId} className="flex items-center justify-between py-2">
                  <span className="font-medium text-slate-800">{m.displayName}</span>
                  {session.canWrite && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => void handleRemove(m.clerkUserId)}
                    >
                      Remove
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Cross-org tab
// ---------------------------------------------------------------------------

function CrossOrgTab({ session }: { session: MtSession }) {
  const [rules, setRules] = useState<MtCrossOrgRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ toOrgId: "", toOrgName: "", fromOrgName: "", adminMapsTo: "tool-viewer", managerMapsTo: "tool-viewer", memberMapsTo: "tool-viewer" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { rules: data } = await listCrossOrg(session);
      setRules(data);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to load cross-org rules");
    } finally {
      setLoading(false);
    }
  }, [session]);

  useEffect(() => { void load(); }, [load]);

  async function handleCreate() {
    setSaving(true);
    try {
      await createCrossOrgRule(session, {
        toOrgId: form.toOrgId || null,
        toOrgName: form.toOrgName || null,
        fromOrgName: form.fromOrgName || null,
        adminMapsTo: form.adminMapsTo,
        managerMapsTo: form.managerMapsTo,
        memberMapsTo: form.memberMapsTo,
      });
      toast.success("Cross-org request submitted (pending approval)");
      setShowCreate(false);
      setForm({ toOrgId: "", toOrgName: "", fromOrgName: "", adminMapsTo: "tool-viewer", managerMapsTo: "tool-viewer", memberMapsTo: "tool-viewer" });
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to create rule");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(rule: MtCrossOrgRule) {
    if (!confirm("Delete this cross-org rule?")) return;
    try {
      await deleteCrossOrgRule(session, rule.id);
      toast.success("Rule deleted");
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to delete rule");
    }
  }

  const statusColors: Record<string, string> = {
    active: "bg-green-100 text-green-700",
    pending: "bg-yellow-100 text-yellow-700",
    rejected: "bg-red-100 text-red-700",
  };

  if (loading) return <div className="py-12 text-center text-sm text-slate-500">Loading…</div>;

  return (
    <div className="space-y-4">
      {session.canWrite && (
        <div className="flex justify-end">
          <Button size="sm" onClick={() => setShowCreate(true)}>+ Request cross-org access</Button>
        </div>
      )}

      {rules.length === 0 && (
        <p className="py-10 text-center text-sm text-slate-400">No cross-org rules configured.</p>
      )}

      <div className="space-y-2">
        {rules.map((r) => (
          <div key={r.id} className="rounded-lg border px-4 py-3">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-slate-800">{r.fromOrgName ?? r.fromOrgId}</span>
                  <span className="text-slate-400">→</span>
                  <span className="font-medium text-slate-800">{r.toOrgName ?? r.toOrgId ?? "All orgs"}</span>
                  <span className={`rounded px-2 py-0.5 text-xs font-medium ${statusColors[r.status] ?? "bg-slate-100"}`}>
                    {r.status}
                  </span>
                </div>
                <p className="text-xs text-slate-400">
                  Role mapping — admin: {r.adminMapsTo} · manager: {r.managerMapsTo} · member: {r.memberMapsTo}
                </p>
              </div>
              {session.canWrite && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-red-600 hover:text-red-700 shrink-0"
                  onClick={() => void handleDelete(r)}
                >
                  Delete
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Request cross-org access</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Your org name (optional)</label>
              <Input
                placeholder="e.g. Acme Ltd"
                value={form.fromOrgName}
                onChange={(e) => setForm((f) => ({ ...f, fromOrgName: e.target.value }))}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Target org ID (leave blank for all)</label>
              <Input
                placeholder="org_…"
                value={form.toOrgId}
                onChange={(e) => setForm((f) => ({ ...f, toOrgId: e.target.value }))}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Target org name (optional)</label>
              <Input
                placeholder="e.g. Partner Corp"
                value={form.toOrgName}
                onChange={(e) => setForm((f) => ({ ...f, toOrgName: e.target.value }))}
              />
            </div>
            {(["adminMapsTo", "managerMapsTo", "memberMapsTo"] as const).map((field) => (
              <div key={field}>
                <label className="mb-1 block text-xs font-medium text-slate-600">
                  {field === "adminMapsTo" ? "Admin" : field === "managerMapsTo" ? "Manager" : "Member"} maps to
                </label>
                <Select value={form[field]} onValueChange={(v) => setForm((f) => ({ ...f, [field]: v }))}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TOOL_ROLES.map((r) => <SelectItem key={r} value={r} className="text-xs">{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={() => void handleCreate()} disabled={saving}>
              {saving ? "Submitting…" : "Submit request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function ManageTeamPage() {
  const { productId } = useParams<{ productId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [session, setSession] = useState<MtSession | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [authPending, setAuthPending] = useState(true);

  useEffect(() => {
    const token = searchParams.get("token");
    if (!token) {
      setError("No auth token found in URL. Please return to your tool and try again.");
      setAuthPending(false);
      return;
    }

    (async () => {
      try {
        const sess = await exchangeToken(token);
        setSession(sess);
        // Remove token from URL bar for security
        setSearchParams((p) => {
          const next = new URLSearchParams(p);
          next.delete("token");
          return next;
        }, { replace: true });
      } catch (e) {
        setError(e instanceof Error ? e.message : "Authentication failed");
      } finally {
        setAuthPending(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (authPending) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-4 border-indigo-600 border-t-transparent" />
          <p className="text-sm text-slate-500">Authenticating…</p>
        </div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <div className="max-w-sm rounded-xl border border-red-200 bg-red-50 p-8 text-center">
          <p className="text-lg font-semibold text-red-700">Access denied</p>
          <p className="mt-2 text-sm text-red-600">{error ?? "Session could not be established."}</p>
          <p className="mt-4 text-xs text-red-400">
            This link may have expired or already been used. Please return to your tool.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="border-b bg-white px-6 py-4">
        <div className="mx-auto max-w-5xl">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600 text-white text-xs font-bold">
                {session.productName.charAt(0).toUpperCase()}
              </div>
              <div>
                <h1 className="text-base font-semibold text-slate-900">
                  Manage Team — {session.productName}
                </h1>
                <p className="text-xs text-slate-400">Org: {session.orgId}</p>
              </div>
            </div>
            {!session.canWrite && (
              <span className="rounded-full border border-slate-200 bg-slate-100 px-3 py-1 text-xs text-slate-500">
                View only — admin access required to make changes
              </span>
            )}
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="mx-auto max-w-5xl px-6 py-8">
        <Tabs defaultValue="members">
          <TabsList className="mb-6">
            <TabsTrigger value="members">Members</TabsTrigger>
            <TabsTrigger value="groups">Groups</TabsTrigger>
            <TabsTrigger value="cross-org">Cross-Org</TabsTrigger>
          </TabsList>
          <TabsContent value="members">
            <div className="rounded-xl border bg-white p-6">
              <MembersTab session={session} />
            </div>
          </TabsContent>
          <TabsContent value="groups">
            <div className="rounded-xl border bg-white p-6">
              <GroupsTab session={session} />
            </div>
          </TabsContent>
          <TabsContent value="cross-org">
            <div className="rounded-xl border bg-white p-6">
              <CrossOrgTab session={session} />
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
